import React from 'react';
import { Accordion, Text, Flex } from '@mantine/core';
import { 
  IconWeight, 
  IconGauge, 
  IconSettings, 
  IconPercentage,
  IconBike,
  IconRotate,
  IconCylinder,
  IconArrowBearLeft2,
  IconArrowBearRight2,
  IconClockCog,
  IconTreadmill,
  IconArmchair2
} from '@tabler/icons-react';
import { UserMachine } from '../../../hooks/useUserMachines';
import colors from '../../../utils/colors';
import { useLanguage } from '../../../context/LanguageContext';

interface MachineAccordionItemProps {
  machine: UserMachine;
  index: number;
}

const MachineAccordionItem: React.FC<MachineAccordionItemProps> = ({
  machine,
  index
}) => {
  const { translate } = useLanguage();
  const getParameterColor = (paramName: string): string => {
    // Color 1
    if (['concentric', 'eccentric', 'brakeW', 'brakeNm'].includes(paramName)) {
      //return '#000000';
      return colors.custom36s;
    }
    // Color 2
    if (['chairHeight', 'chairDepth'].includes(paramName)) {
      //return '#17c900';
      return colors.custom36p;
    }
    // Color 3
    if (['limitFlex', 'limitExt'].includes(paramName)) {
      //return '#e27900';
      return colors.custom36p;
    }
    // Color 4
    if (['rollerLength', 'loadRatio'].includes(paramName)) {
      //return '#535353';
      return colors.custom36p;
    }
    // Color 5
    if (['mode', 'timer'].includes(paramName)) {
      //return '#e27900';
      return colors.custom36p;
    }
    // Default
    return '#000000';
  };

  const getParameterIcon = (paramName: string) => {
    const iconColor = getParameterColor(paramName);
    switch (paramName) {
      case 'concentric':
      case 'eccentric':
        return <IconWeight size={22} color={iconColor} />;
      case 'brakeW':
      case 'brakeNm':
        return <IconGauge size={22} color={iconColor} />;
      case 'chairHeight':
      case 'chairDepth':
        return <IconArmchair2  size={18} color={iconColor} />;
      case 'limitFlex':
        return <IconArrowBearLeft2 size={18} color={iconColor} />;
      case 'limitExt':
        return <IconArrowBearRight2 size={18} color={iconColor} />;
      case 'rollerLength':
        return <IconCylinder size={18} color={iconColor} />;
      case 'loadRatio':
        return <IconPercentage size={18} color={iconColor} />;
      case 'trackType':
      case 'bikeType':
      case 'slopeType':
        return <IconBike size={18} color={iconColor} />;
      case 'mode':
        return <IconTreadmill size={18} color={iconColor} />;
      case 'timer':
        return <IconClockCog   size={18} color={iconColor} />;
      case 'tbdParam':
        return <IconRotate size={18} color={iconColor} />;
      default:
        return <IconSettings size={18} color={iconColor} />;
    }
  };

  const getParameterDisplayName = (paramName: string): string => {
    const displayNames: { [key: string]: string } = {
      concentric: translate('machineConcentric'),
      eccentric: translate('machineEccentric'),
      limitFlex: translate('machineLimitFlex'),
      limitExt: translate('machineLimitExt'),
      chairHeight: translate('machineChairHeight'),
      chairDepth: translate('machineChairDepth'),
      rollerLength: translate('machineRollerLength'),
      loadRatio: translate('machineLoadRatio'),
      brakeW: translate('machineBrakeW'),
      brakeNm: translate('machineBrakeNm'),
      trackType: translate('machineTrackType'),
      bikeType: translate('machineBikeType'),
      slopeType: translate('machineSlopeType'),
      mode: translate('machineMode'),
      timer: translate('machineTimer'),
      tbdParam: translate('machineTbdParam'),
      powerTypeNm: translate('machinePowerTypeNm'),
      reps: translate('machineReps')
    };
    
    return displayNames[paramName] || paramName;
  };

  const getParameterUnit = (paramName: string, value: any): string => {
    if (typeof value === 'boolean') return '';
    
    const units: { [key: string]: string } = {
      concentric: ' kg',
      eccentric: ' kg',
      limitFlex: ' °',
      limitExt: ' °',
      chairHeight: ' cm',
      chairDepth: ' cm',
      rollerLength: ' cm',
      loadRatio: ' %',
      brakeW: ' W',
      brakeNm: ' Nm',
      timer: ' s',
      mode: ' ',
    };
    
    return units[paramName] || '';
  };

  const formatParameterValue = (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Tak' : 'Nie';
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return String(value);
  };

  const nonZeroParameters = Object.entries(machine.parameters).filter(([key, value]) => {
    if (key === 'powerTypeNm' || key === 'reps') return false; // Exclude powerTypeNm and reps parameters
    if (typeof value === 'boolean') return true; // Show all boolean values
    if (typeof value === 'number') return value !== 0; // Only show non-zero numbers
    return value; // Show truthy values for other types
  });

  // Priority order for parameters
  const getParameterPriority = (paramName: string): number => {
    const priorities: { [key: string]: number } = {
      // Most important
      'concentric': 1,
      'eccentric': 2,
      // Cardio power values
      'brakeW': 1,
      'brakeNm': 2,
      // Chair adjustments
      'chairHeight': 10,
      'chairDepth': 11,
      // Limits
      'limitFlex': 20,
      'limitExt': 21,
      // Physical adjustments
      'rollerLength': 30,
      'loadRatio': 31,
      // Cardio settings
      'trackType': 40,
      'bikeType': 41,
      'slopeType': 42,
      // Least priority
      'timer': 50,
      'mode': 51,
      'tbdParam': 60,
      'powerTypeNm': 61,
      'reps': 62
    };
    return priorities[paramName] || 99; // Default high priority for unknown params
  };

  // Sort parameters by priority
  const sortedParameters = nonZeroParameters.sort(([a], [b]) => 
    getParameterPriority(a) - getParameterPriority(b)
  );

  const importantParams = sortedParameters.filter(([paramName]) => 
    ['concentric', 'eccentric', 'brakeW', 'brakeNm'].includes(paramName)
  );
  const otherParams = sortedParameters.filter(([paramName]) => 
    !['concentric', 'eccentric', 'brakeW', 'brakeNm'].includes(paramName)
  );

  if (sortedParameters.length === 0) {
    return null;
  }

  return (
    <Accordion.Item 
      value={`machine-${machine.id}`}
      mb={'xs'}
      style={{ 
        borderLeft: `5px solid ${colors.custom36p}`,
        backgroundColor: 'white'
      }}
    >
      <Accordion.Control style={{ padding: '2px 16px' }}>
        <Flex justify="space-between" align="center" w="100%">
          <Flex direction="column" className="w-full">
            <Text fw={600} c={colors.custom36s} size="md">
              {machine.displayName}
            </Text>
          </Flex>
        </Flex>
      </Accordion.Control>
      <Accordion.Panel pt="xs" pb="xs">
        <Flex direction="column" gap="md">
          {/* First row: Important parameters */}
          {importantParams.length > 0 && (
            <Flex justify="center" gap="lg" wrap="wrap">
              {importantParams.map(([paramName, value]) => (
                <Flex 
                  key={paramName} 
                  direction="column" 
                  align="center" 
                  className="w-20"
                >
                  <Flex align="center" gap={6} mb={4}>
                    {getParameterIcon(paramName)}
                    <Text 
                      size="22px"
                      fw={700} 
                      style={{whiteSpace: 'nowrap'}}
                      c={getParameterColor(paramName)}
                      mt={-2}
                    >
                      {formatParameterValue(value)}{getParameterUnit(paramName, value)}
                    </Text>
                  </Flex>
                  <Text size="xs" c="dimmed" ta="center" style={{whiteSpace: 'nowrap'}}>
                    {getParameterDisplayName(paramName)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
          
          {/* Second row: Other parameters */}
          {otherParams.length > 0 && (
            <Flex justify="center" gap="md" wrap="wrap">
              {otherParams.map(([paramName, value]) => (
                <Flex 
                  key={paramName} 
                  direction="column" 
                  align="center" 
                  className="w-20"
                >
                  <Flex align="center" gap={6} mb={4}>
                    {getParameterIcon(paramName)}
                    <Text 
                      size="18px"
                      fw={700} 
                      style={{whiteSpace: 'nowrap'}}
                      c={getParameterColor(paramName)}
                    >
                      {formatParameterValue(value)}{getParameterUnit(paramName, value)}
                    </Text>
                  </Flex>
                  <Text size="xs" c="dimmed" ta="center" style={{whiteSpace: 'nowrap'}}>
                    {getParameterDisplayName(paramName)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default MachineAccordionItem;